import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const navLinks = [
  { path: '/', label: 'Home' },
  { path: '/features', label: 'Features' },
  { path: '/pricing', label: 'Pricing' },
  { path: '/about', label: 'About' },
  { path: '/blog', label: 'Blog' },
  { path: '/contact', label: 'Contact' },
];

export default function MarketingNav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeHash, setActiveHash] = useState('');
  const location = useLocation();
  const isHome = location.pathname === '/';
  const isDark = false;

  const handleNavClick = (e, path) => {
    if (path.includes('#')) {
      e.preventDefault();
      const hash = path.split('#')[1];
      setActiveHash(hash);
      setMobileOpen(false);
      if (location.pathname === '/') {
        document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate('/' + '#' + hash);
        setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' }), 500);
      }
    } else {
      setActiveHash('');
      setMobileOpen(false);
    }
  };

  // Clear active hash when navigating to non-home pages
  useEffect(() => {
    if (location.pathname !== '/') setActiveHash('');
  }, [location.pathname]);

  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location]);

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/80 backdrop-blur-xl border-b border-indigo-100/50 shadow-sm' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <img src={isDark ? "/logo-white.svg" : "/logo.svg"} alt="PAFHub" className="w-9 h-9" />
              <span className="font-bold text-lg tracking-tight"><span className={isDark ? 'text-white' : 'text-[#000000]'}>PAF</span><span className={isDark ? 'text-white/80' : 'text-[#4F46E5]'}>Hub</span></span>
            </Link>

            {/* Desktop nav */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={(e) => handleNavClick(e, link.path)}
                  className={`relative px-4 py-2 text-sm font-medium transition-colors ${
                    (() => {
                      const linkHash = link.path.includes('#') ? link.path.split('#')[1] : null;
                      const isActive = linkHash ? (activeHash === linkHash) : (!activeHash && location.pathname === link.path);
                      return isActive ? 'text-[#4F46E5]' : 'text-[#64748B] hover:text-[#4F46E5]';
                    })()
                  }`}
                  style={{
                    backgroundImage: (() => {
                      const linkHash = link.path.includes('#') ? link.path.split('#')[1] : null;
                      const isActive = linkHash ? (activeHash === linkHash) : (!activeHash && location.pathname === link.path);
                      return isActive ? 'linear-gradient(#4F46E5, #4F46E5)' : undefined;
                    })(),
                    backgroundSize: '60% 2px',
                    backgroundPosition: 'center bottom',
                    backgroundRepeat: 'no-repeat',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundImage = 'linear-gradient(#4F46E5, #4F46E5)'; }}
                  onMouseLeave={(e) => {
                    const linkHash = link.path.includes('#') ? link.path.split('#')[1] : null;
                    const isActive = linkHash ? (activeHash === linkHash) : (!activeHash && location.pathname === link.path);
                    if (!isActive) e.currentTarget.style.backgroundImage = 'none';
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* CTA */}
            <div className="hidden lg:flex items-center gap-3">
              <Link to="/login" className={`px-5 py-2.5 text-sm font-semibold transition-colors ${isDark ? 'text-white hover:text-white/80' : 'text-[#0F172A] hover:text-indigo-600'}`}>
                Sign In
              </Link>
              <Link to="/login" className="px-5 py-2.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl transition-all shadow-md hover:shadow-lg hover:shadow-indigo-500/25 active:scale-[0.98]">
                Get Started
              </Link>
            </div>

            {/* Mobile toggle */}
            <button onClick={() => setMobileOpen(!mobileOpen)} className={`lg:hidden p-2 rounded-lg ${isDark ? 'text-white hover:bg-white/10' : 'text-[#0F172A] hover:bg-slate-100'}`}>
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="fixed inset-0 z-40 lg:hidden"
          >
            <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
            <div className="absolute top-16 left-0 right-0 bg-white border-b border-indigo-100/50 shadow-lg p-6 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={(e) => handleNavClick(e, link.path)}
                  className={`block px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                    location.pathname === link.path ? 'text-indigo-600 bg-indigo-50' : 'text-[#0F172A] hover:bg-slate-50'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-4 border-t border-indigo-50 space-y-2">
                <Link to="/login" className="block px-4 py-3 text-sm font-semibold text-[#0F172A] hover:bg-slate-50 rounded-xl">Sign In</Link>
                <Link to="/login" className="block px-4 py-3 text-sm font-semibold text-white bg-indigo-600 rounded-xl text-center">Get Started</Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
